/*
 * pdp-redesign-sticky-footer.js
 * -----------------------------
 * ENG-1840. A SEPARATE bar that slides up from the bottom of the screen once
 * the page's own add-to-cart button has scrolled away, and slides back down
 * when you return to it or reach the site footer.
 *
 * THIS IS A REWRITE, NOT A TWEAK. The previous build physically reparented the
 * real CTA between an in-flow slot and a fixed slot — "the button gets picked
 * up as you scroll by". ENG-1840 replaces that model outright:
 *
 *   "Instead of having the button get picked up as you scroll by ... The button
 *    that sits below the dropdowns will stay on the page at all times."
 *
 * So the button no longer moves, and everything the old file existed to solve —
 * reparenting, the style flush before the class change, the hysteresis-gap
 * correction, the clip-path wiggle reveal, the three-state machine — is gone
 * with it. What is left is a fixed bar with one transform on it.
 *
 * The wave's drop shadow survives, but as a STATIC part of the panel rather
 * than something that animates in: the bar arrives in one piece, so there is
 * nothing for it to reveal.
 *
 * SUPERSEDED, deliberately: docs/sticky-footer/engineering-spec.md and
 * scroll-prototype.html described the pick-up model and no longer apply to this
 * file. docs/sticky-footer/eng-1840-prototype.html is the vendored copy of the
 * new handoff prototype and is the source of truth for the trigger and the
 * animation; the bar's own layout comes from Figma 1118:25389.
 *
 * WHAT IS CARRIED OVER from the old file, because it was measured and is still
 * right:
 *
 *   • The wave is GENERATED, not an <img>. The asset is drawn at one fixed
 *     width with preserveAspectRatio="none", so stretching it to the screen
 *     scales the stroke non-uniformly and the line reads as lumpy. Building the
 *     path against the element's real pixel width pins the motif to 1:1 CSS
 *     pixels on every device.
 *   • WAVELENGTH 21.75 / AMP 4 / STROKE_W 3, all sampled from the design rather
 *     than from the old prototype (which had 17 / 4 / 4.5).
 *   • Two SVG paths sharing one curve — one filled, one stroked. Fill and
 *     stroke on a single closed path draws the line down the sides and along
 *     the bottom too, not just the top edge.
 *
 * THE BUTTON IN HERE IS A PROXY. It forwards its click to the real CTA rather
 * than running its own add-to-cart. pdp-bundler.js binds add-to-cart to that
 * element, and the payload it builds is the bundler's full unit state — there
 * is no second copy of that logic to keep in sync, and the dataLayer contract
 * in docs/TRACKING_DATALAYER.md fires from exactly the same place either way.
 */
(function () {
  var AMP = 4;
  /* MEASURED FROM THE DESIGN. Sampled from the Figma export by tracing the
     stroke's top edge per column and counting upward mean-crossings: 21.76px
     between cycles, amplitude exactly 4px peak-to-trough. */
  var WAVELENGTH = 21.75;
  /* "Adjust to 3 px stroke" (926:19698). Measure what paints, not what the
     static divider asset declares. */
  var STROKE_W = 3;
  // Fallback only. The real width is measured per render — see renderWave.
  var VIEW_W = 400;

  /* The path runs past both edges of the SVG viewport, which clips it. One full
     WAVELENGTH plus margin, so a seam can never be exposed at either edge. */
  var OVERHANG = 32;
  /* THE WAVE'S DROP SHADOW — a second copy of the panel offset up and to the
     right, drawn behind the opaque base copy so only a sliver shows along the
     crest. Figma 1118:25389 draws it; it reads as the grey wave riding above
     the black one.

     ⚠️ NOT `filter: drop-shadow()`. WEBKIT SILENTLY DROPS A CSS FILTER APPLIED
     TO AN SVG <g>: getComputedStyle reads the value back correctly and nothing
     ever paints, so it looks right in Chrome, right in the computed styles, and
     blank in Safari — roughly half the traffic on a mobile-first PDP. Built out
     of a duplicated path instead, which every engine supports. The spec's blur
     of 0 is what makes a hard offset equivalent rather than an approximation. */
  var SHADOW_DX = 4;
  var SHADOW_DY = -2;

  /* BLANK viewBox ROWS ABOVE THE WAVE, so the shadow is not clipped.

     An <svg> clips to its viewBox (overflow:hidden is the UA default). The
     shadow sits SHADOW_DY above the base copy, so without headroom its crests
     are shaved off flat — a bug this footer has already shipped once.

     3 = the 2px lift plus a pixel of margin. The bar is that much taller than
     its 100px visual height to accommodate it, and .pdpr-sticky__row's top
     padding absorbs the difference, so nothing moves on screen and the parked
     transform still hides the shadow along with everything else. Keep HEADROOM,
     the bar's height and that padding in step; they are one measurement. */
  var HEADROOM = 3;

  /* The curve's crest apex lands at WAVE_TOP + 2, NOT at WAVE_TOP: these are
     quadratic Béziers, and a quadratic only reaches half way to its control
     point — apex = (P0 + 2·P1 + P2) / 4, which works out to WAVE_TOP + 2 for a
     4px amplitude. Getting this wrong is a silent 2px, so it is spelled out.

     0.5 before the headroom puts the apex at 2.5 and the ink's top edge at 1.0
     once half the 3px stroke is counted, which is what Figma 1118:25389 draws:
     the wave group's box starts at y=75 with the artwork inset -2.58% above it,
     i.e. the box top IS the crest apex and the stroke rides higher. */
  var WAVE_TOP = 0.5 + HEADROOM;

  /* ENG-1840, verbatim: transform: translateY(), 660ms,
     cubic-bezier(.32, .72, 0, 1). Both directions, unlike the old build's
     asymmetric reveal/retract pair — the ticket gives one duration and the
     handoff prototype ships exactly this one line. */
  var SLIDE_MS = 660;
  var SLIDE_EASE = 'cubic-bezier(.32, .72, 0, 1)';

  /* Dead zone around the show/hide line. The prototype uses a single threshold;
     trackpad momentum and iOS rubber-banding sit exactly on it and make the bar
     flap in and out. Same reasoning as the old build's hysteresis, same value. */
  var HYSTERESIS = 12;

  // The desktop design (707:19618) has no sticky footer at all — the CTA lives
  // in the buy-box card and scrolls with the page.
  var MOBILE_MAX = 1023;

  /* dx/dy shift the curve INSIDE the viewBox. That is how the shadow copy gets
     its offset, rather than a transform on the <svg> element — see renderWave.
     The path is generated from -OVERHANG, so a few px of dx still leaves ~28px
     of material past the left edge and the seam stays off-screen. */
  function buildWavePath(amplitude, wavelength, viewW, dx, dy) {
    dx = dx || 0;
    dy = dy || 0;
    var half = wavelength / 2;
    var mid = amplitude + WAVE_TOP + dy;
    var d = 'M' + (-OVERHANG + dx) + ',' + mid;
    var x = -OVERHANG;
    var up = true;
    while (x < viewW + OVERHANG) {
      var nx = x + half;
      var cy = (up ? WAVE_TOP : amplitude * 2 + WAVE_TOP) + dy;
      d += ' Q' + (x + half / 2 + dx) + ',' + cy + ' ' + (nx + dx) + ',' + mid;
      x = nx;
      up = !up;
    }
    return d;
  }

  function buildPanelPath(amplitude, wavelength, viewW, panelH, dx, dy) {
    dx = dx || 0;
    return buildWavePath(amplitude, wavelength, viewW, dx, dy) +
      ' L' + (viewW + OVERHANG + dx) + ',' + panelH +
      ' L' + (-OVERHANG + dx) + ',' + panelH + ' Z';
  }

  /* One curve, TWO paths — one filled, one stroked. Fill and stroke on a single
     closed path draws the line down the sides and along the bottom too, not
     just the top edge. */
  function svg(cls) {
    var el = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    el.setAttribute('class', 'pdpr-sticky__svg ' + cls);
    el.setAttribute('viewBox', '0 0 400 100');
    el.setAttribute('preserveAspectRatio', 'none');
    el.setAttribute('aria-hidden', 'true');
    /* The 25% lives on the <g>, not on the two paths: the stroke overlaps the
       fill along the wave, and two 25% alphas composite to ~44% there, drawing
       a darker seam along the crest. Group opacity flattens first. */
    var g = document.createElementNS('http://www.w3.org/2000/svg', 'g');
    var fill = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    fill.setAttribute('class', 'pdpr-sticky__fill');
    var stroke = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    stroke.setAttribute('class', 'pdpr-sticky__stroke');
    g.appendChild(fill);
    g.appendChild(stroke);
    el.appendChild(g);
    return el;
  }

  function init() {
    if (!document.documentElement.classList.contains('pdp-redesign')) return;

    /* No bar in Firefox for iOS. When its toolbar shrinks it keeps drawing the
       page in the ~60px beneath, but reports none of that space to the page and
       clips fixed elements at the line it does report, so the bar floats with
       content scrolling underneath and nothing here can paint over it
       (firefox-ios #35935). The page's own button is unaffected. */
    if (/FxiOS/.test(navigator.userAgent)) return;

    var cta = document.querySelector('[data-pdp-cta]');
    if (!cta || cta.dataset.pdprStickyReady === 'true') return;
    cta.dataset.pdprStickyReady = 'true';

    /* ---- Structure ------------------------------------------------------
       Appended to <body> so `position: fixed` resolves against the viewport —
       inside a transformed ancestor it would resolve against that instead.

       NOTHING is moved out of the page: the button and the shipping line
       stay exactly where the bundler rendered them. */
    var bar = document.createElement('div');
    bar.className = 'pdpr-sticky';
    // Set here rather than left to the first apply(): the bar is born parked,
    // so apply() sees no state CHANGE and returns before it can mark it up.
    bar.setAttribute('aria-hidden', 'true');
    if ('inert' in HTMLElement.prototype) bar.inert = true;

    var panel = document.createElement('div');
    panel.className = 'pdpr-sticky__panel';
    // Shadow first, so the opaque base copy paints over all of it except the
    // sliver the offset exposes along the crest.
    var shadowSvg = svg('pdpr-sticky__svg--shadow');
    var baseSvg = svg('pdpr-sticky__svg--base');
    panel.appendChild(shadowSvg);
    panel.appendChild(baseSvg);

    /* Cream below the bar's own bottom edge. `position: fixed; bottom: 0`
       resolves against the LAYOUT viewport; on iOS Safari the visual viewport
       is taller than that whenever the toolbars are collapsing or the page is
       rubber-banding, and for those frames page content shows through under the
       bar. There is no way to pin to the visual viewport from CSS, so the fix
       is to have something to spare.

       Absolutely positioned at top:100% so it adds nothing to the bar's height
       — which matters, because the hidden state is translateY(100%) and any
       height this added would be extra distance the slide has to travel. Parked
       below the screen in the hidden state for the same reason. */
    var bleed = document.createElement('div');
    bleed.className = 'pdpr-sticky__bleed';

    var row = document.createElement('div');
    row.className = 'pdpr-sticky__row';

    /* PRICE OUT OF THE BUTTON — the point of the redesign. ENG-1840: "move the
       price out of the button in the sticky footer only to help better display
       the discount + call attention to the CTA". The page's own button shows
       no price (it is on the cards); this copy shows it beside the button. */
    var priceWrap = document.createElement('div');
    priceWrap.className = 'pdpr-sticky__price';
    var priceNow = document.createElement('span');
    priceNow.className = 'pdpr-sticky__price-now';
    var priceWas = document.createElement('s');
    priceWas.className = 'pdpr-sticky__price-was';
    priceWas.hidden = true;
    priceWrap.appendChild(priceNow);
    priceWrap.appendChild(priceWas);

    var proxy = document.createElement('button');
    proxy.type = 'button';
    proxy.className = 'pdpr-sticky__cta';
    var proxyLabel = document.createElement('span');
    proxyLabel.className = 'pdpr-sticky__cta-label';
    proxy.appendChild(proxyLabel);

    row.appendChild(priceWrap);
    row.appendChild(proxy);
    bar.appendChild(panel);
    bar.appendChild(bleed);
    bar.appendChild(row);
    document.body.appendChild(bar);


    bar.style.transition = 'transform ' + SLIDE_MS + 'ms ' + SLIDE_EASE;

    // Not a second add-to-cart. See the header: pdp-bundler.js owns the
    // payload and the tracking, and it is bound to the real button.
    proxy.addEventListener('click', function () {
      /* PIN THE WIDTH FIRST. Unlike the page's full-width button this one is
         sized by its label, and ENG-1856's "Adding..." is ~25px narrower than
         "Add 2 to cart" — so without this the button visibly shrinks under the
         thumb that just tapped it. Figma draws 1118:25524 and 1118:25702 at the
         same 180px, i.e. the box is not meant to move. Cleared again by sync()
         if the add fails and the label goes back. */
      proxy.style.width = proxy.getBoundingClientRect().width + 'px';
      cta.click();
    });

    /* ---- Mirror the real CTA ---------------------------------------------
       The bundler rewrites the label ("Add 2 to cart"), the price, the struck
       original and the button's colourway every time a slot changes, all on the
       real button. Reading them back off that element keeps one source of
       truth; recomputing them here would mean a second copy of the bundler's
       total/discount maths that could drift. */
    var labelEl = cta.querySelector('[data-pdp-cta-label]');
    var priceEl = cta.querySelector('[data-pdp-cta-price]');
    var wasEl = cta.querySelector('[data-pdp-cta-was]');

    function sync() {
      proxyLabel.textContent = labelEl ? labelEl.textContent : 'Add to cart';
      proxy.setAttribute('aria-label', proxyLabel.textContent);
      priceNow.textContent = priceEl ? priceEl.textContent : '';
      if (wasEl && !wasEl.hidden && wasEl.textContent) {
        priceWas.textContent = wasEl.textContent;
        priceWas.hidden = false;
      } else {
        priceWas.hidden = true;
      }
      // Colourway. The bundler sets these inline on the real button, so read
      // the inline values rather than the computed ones — computed would also
      // pick up the stylesheet's default and pin it here permanently.
      proxy.style.backgroundColor = cta.style.backgroundColor;
      proxy.style.color = cta.style.color;
      /* ENG-1856's loading state. It rides the mirror for free — the label is
         already copied from the real button, so "Adding..." arrives on its own
         — but the class has to come across too, because the stroke and shadow
         are this element's own. This is the button the shopper actually
         tapped; the real one is off-screen above by the time it matters. */
      var isAdding = cta.classList.contains('is-adding');
      proxy.classList.toggle('is-adding', isAdding);
      // Release the width pinned at click time, so a later label change (a
      // colour added, "Add 3 to cart") is not clipped by a stale width.
      if (!isAdding) proxy.style.width = '';
    }
    sync();
    new MutationObserver(sync).observe(cta, {
      childList: true,
      characterData: true,
      subtree: true,
      attributes: true,
      // 'class' is how the ENG-1856 loading state reaches this copy.
      attributeFilter: ['style', 'hidden', 'class']
    });

    /* ---- Wave ------------------------------------------------------------
       The viewBox is sized to the element's ACTUAL width, not a fixed 400.
       With preserveAspectRatio="none" a constant viewBox stretches the wave by
       elementWidth/400, so the motif's wavelength changed with the screen —
       ~13.6px on a 320px phone against ~18.3px on a 430px one, for a design
       drawn at a single fixed size. */
    /* THE SHADOW'S OFFSET IS IN THE PATH, NOT A TRANSFORM ON THE <svg>.

       Translating the element moves its viewBox with it, and an <svg> clips to
       that — so shifting the shadow 4px right left the leftmost 4px of the
       screen with no shadow at all, a visible notch at the corner where the
       wave met the edge. Shifting the CURVE inside a viewBox that stays put
       draws from the path's own overhang instead, and there is nothing to
       clip. */
    function renderWave() {
      var rect = panel.getBoundingClientRect();
      var h = Math.round(rect.height) || 100;
      var w = Math.round(rect.width) || VIEW_W;
      [
        { svg: shadowSvg, dx: SHADOW_DX, dy: SHADOW_DY },
        { svg: baseSvg, dx: 0, dy: 0 }
      ].forEach(function (copy) {
        copy.svg.setAttribute('viewBox', '0 0 ' + w + ' ' + h);
        copy.svg.querySelector('.pdpr-sticky__fill')
          .setAttribute('d', buildPanelPath(AMP, WAVELENGTH, w, h, copy.dx, copy.dy));
        var st = copy.svg.querySelector('.pdpr-sticky__stroke');
        st.setAttribute('d', buildWavePath(AMP, WAVELENGTH, w, copy.dx, copy.dy));
        st.setAttribute('stroke-width', STROKE_W);
      });
    }
    new ResizeObserver(renderWave).observe(panel);
    renderWave();

    /* ---- When the bar is on screen ---------------------------------------
       SHOW once the page's own button has left the top of the screen. The
       handoff prototype is one line — `scrollTop > buttonBottom` against a
       button whose bottom edge is at a known document offset — which is the
       button's bottom edge passing the top of the viewport. Measured live off
       the button instead of a constant, so it survives the bundler inserting
       colour rows above the CTA and changing its offset.

       HIDE again on either of the ticket's two conditions:
         • "You scroll back up and get to the static button" — the same line,
           crossed the other way.
         • "You get the footer and then it will slide back down" — the SITE
           footer, not the next section. The old build dismissed at the next
           section; the intent video shows the bar riding all the way through
           the plans section and beyond, so that reading is wrong here. */
    var siteFooter = document.querySelector('.footer');
    var visible = false;

    function shouldShow() {
      if (window.innerWidth > MOBILE_MAX) return false;

      // Each boundary widens in whichever direction keeps the CURRENT state, so
      // scroll jitter sitting exactly on a line cannot flap the animation.
      var ctaBottom = cta.getBoundingClientRect().bottom;
      var gone = visible ? ctaBottom <= HYSTERESIS : ctaBottom <= 0;
      if (!gone) return false;

      if (siteFooter) {
        var footerTop = siteFooter.getBoundingClientRect().top;
        var reached = visible
          ? footerTop <= window.innerHeight - HYSTERESIS
          : footerTop <= window.innerHeight + HYSTERESIS;
        if (reached) return false;
      }

      return true;
    }

    function apply() {
      var next = shouldShow();
      if (next === visible) return;
      visible = next;
      bar.classList.toggle('is-visible', visible);
      // A bar parked below the screen is still in the tab order, so a keyboard
      // user could otherwise land on an add-to-cart they cannot see — and this
      // one is a duplicate, so unlike the old docked button there is nothing
      // lost by taking it out. The real CTA is always reachable in the page.
      bar.setAttribute('aria-hidden', visible ? 'false' : 'true');
      if ('inert' in HTMLElement.prototype) bar.inert = !visible;
    }
    apply();

    var queued = false;
    function schedule() {
      if (queued) return;
      queued = true;
      requestAnimationFrame(function () {
        queued = false;
        apply();
      });
    }

    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);

    /* ---- Track the VISUAL viewport --------------------------------------
       iOS Safari's layout viewport is taller than the visual one while the
       bottom address bar is expanded, and `position: fixed; bottom: 0` anchors
       to the layout one. The bar then sits behind the address bar with page
       content visible in the difference — worst on first load, because nothing
       has collapsed the address bar yet, which is exactly when it was reported.

       The cream bleed below the bar was the first attempt at this and was not
       enough: it hides the difference rather than removing it, and only as far
       as its own height.

       The inset is what the layout viewport has and the visual one does not,
       below the fold: innerHeight - (visual height + how far the visual
       viewport has been pushed down). Clamped at 0 — rubber-banding makes it
       briefly negative, and a negative would lift the bar off the bottom edge
       on every over-scroll.

       Writes a CSS VARIABLE rather than a style on the element, so it composes
       with the transform the slide animation owns instead of fighting it.

       No visualViewport (older Safari, some in-app browsers) means the variable
       is never set and the CSS fallback of 0px applies — exactly the behaviour
       before this existed. */
    /* ---- ?pdp_vvdebug=1 -------------------------------------------------
       A live readout of every number in the paragraph above, because this bug
       cannot be reproduced on any machine we can run a test on. Reads only —
       it changes nothing about the bar.

       Pinned to the TOP, and deliberately not to the bottom: a fixed element
       with a background within 3px of the bottom edge would become Safari 26's
       toolbar tint source and quietly replace the one being debugged. */
    if (/[?&]pdp_vvdebug=1/.test(location.search)) {
      var dbg = document.createElement('div');
      dbg.setAttribute('aria-hidden', 'true');
      dbg.style.cssText =
        'position:fixed;top:90px;left:8px;z-index:9999;display:block;' +
        'font:11px/1.45 ui-monospace,Menlo,monospace;white-space:pre;' +
        'background:rgba(0,0,0,.82);color:#0f0;padding:6px 8px;' +
        'border-radius:6px;pointer-events:none';
      document.body.appendChild(dbg);
      var paint = function () {
        var v = window.visualViewport;
        var b = bar.getBoundingClientRect();
        var lh = Math.max(document.documentElement.clientHeight || 0, window.innerHeight || 0);
        var raw = v ? lh - (v.height + v.offsetTop) : 0;   // reported, not acted on
        dbg.textContent =
          'innerH    ' + window.innerHeight +
          '\nclientH   ' + document.documentElement.clientHeight +
          '\nvv.height ' + (v ? v.height.toFixed(2) : 'n/a') +
          '\nvv.offset ' + (v ? v.offsetTop.toFixed(2) : 'n/a') +
          '\nraw inset ' + raw.toFixed(2) +
          '\nbottom    ' + getComputedStyle(bar).bottom +
          '\nbar.bottom ' + b.bottom.toFixed(2) +
          '\nGAP       ' + (window.innerHeight - b.bottom).toFixed(2);
        requestAnimationFrame(paint);
      };
      requestAnimationFrame(paint);
    }
    new ResizeObserver(schedule).observe(document.body);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  // The class is applied by hand during local QA, after load.
  new MutationObserver(init).observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['class']
  });
})();

/*
 * THE TINT STRIP FOLLOWS THE PAGE.
 *
 * .pdpr-tint (layout/theme.liquid, styled in pdp-redesign-head) is the fixed
 * strip iOS 26 Safari reads its toolbar colour from. It used to be cream, always,
 * so wherever a section of another colour scrolled behind the bottom of the
 * screen — the yellow plans band, the FAQ boxes, the footer — it showed as a
 * pale line above the toolbar.
 *
 * Now it takes the background colour of the section just above it: from the
 * element under that point, walking up to the first near-full-width element
 * with an opaque background-color. Safari re-reads a fixed container's background-color from
 * style as the page scrolls, so the toolbar follows too. The server-rendered
 * cream stays as the first-paint value, which is what the cold-load fix
 * depends on; this only changes it after that.
 *
 * iOS WebKit only, the same gate as the CSS that shows the strip. Both arms.
 */
(function () {
  if (!window.CSS || !CSS.supports('-webkit-touch-callout', 'none')) return;
  if (!document.documentElement.classList.contains('pdp-redesign')) return;

  var strip = document.querySelector('.pdpr-tint');
  if (!strip) return;

  var SKIP = '.pdpr-tint, .pdpr-sticky, #PBarNextFrameWrapper, #PBarNextFrame';
  var last = '';
  var queued = false;

  /* Section backgrounds only: an element counts once it spans nearly the full
     width. Otherwise a button, a swatch or a plans card passing the bottom edge
     would flash the toolbar its colour mid-scroll. */
  function opaqueBackground(el) {
    var minWidth = window.innerWidth * 0.95;
    for (; el && el.nodeType === 1; el = el.parentElement) {
      if (el.getBoundingClientRect().width < minWidth) continue;
      var bg = getComputedStyle(el).backgroundColor;
      var m = bg.match(/rgba?\(([^)]+)\)/);
      if (!m) continue;
      var parts = m[1].split(',');
      var alpha = parts.length > 3 ? parseFloat(parts[3]) : 1;
      if (alpha >= 0.9) return bg;
    }
    return '';
  }

  function update() {
    queued = false;
    var box = strip.getBoundingClientRect();
    if (!box.height) return;
    var y = Math.max(0, box.top - 1);
    var stack = document.elementsFromPoint(window.innerWidth / 2, y);
    var under = null;
    for (var i = 0; i < stack.length; i++) {
      if (!stack[i].closest(SKIP)) { under = stack[i]; break; }
    }
    var colour = opaqueBackground(under) || getComputedStyle(document.documentElement).backgroundColor;
    if (colour && colour !== last) {
      last = colour;
      strip.style.backgroundColor = colour;
    }
  }

  function queue() {
    if (queued) return;
    queued = true;
    requestAnimationFrame(update);
  }

  window.addEventListener('scroll', queue, { passive: true });
  window.addEventListener('resize', queue);
  // Accordions and the colour drawer change what sits at the bottom without a
  // scroll.
  document.addEventListener('click', function () { setTimeout(queue, 350); });
  window.addEventListener('load', queue);
  queue();
})();
